import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, typography } from '../../../styles/theme';
import { ContentSection, SectionTitle, Grid, InfoCard, InfoLabel, InfoValue } from '../common/StyledComponents';
import Dialog, { 
  SuccessDialog, 
  SuccessTitle, 
  SuccessButtonGroup,
  OkButton, 
  UndoButton,
  CancelButton,
  SaveButton,
  Overlay
} from '../dialogs/Dialog';

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  color: ${colors.text.secondary};
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.primary};
  }

  ${InfoCard}:hover & {
    opacity: 1;
  }
`;

const InfoCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PaymentOptionButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${props => props.selected ? colors.primary + '10' : 'white'};
  border: 2px solid ${props => props.selected ? colors.primary : colors.border};
  border-radius: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;
  
  &:hover {
    border-color: ${colors.primary};
    background: ${colors.primary + '10'};
  }
`;

const PaymentDialog = ({ isOpen, onClose, onSave, field, currentValue }) => {
  const [selectedOption, setSelectedOption] = useState(currentValue);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
    }
  }, [isOpen]);

  const options = {
    schedule: ['Annual', 'Monthly', 'Quarterly', 'Semi-Annual'],
    method: ['Direct Debit', 'Credit Card', 'Bank Transfer', 'Cheque'],
    nextPayment: ['2024-02-01', '2024-03-01', '2024-04-01', '2024-05-01']
  };

  const titles = {
    schedule: 'Payment Schedule',
    method: 'Payment Method',

  };

  const handleSave = () => {
    onSave(field, selectedOption);
    setShowSuccess(true);
  };

  const handleClose = () => {
    setShowSuccess(false);
    onClose();
  };

  const handleUndo = () => {
    setSelectedOption(currentValue);
    setShowSuccess(false);
  };

  if (!isOpen) return null;

  return (
    <Overlay>
      {showSuccess ? (
        <SuccessDialog>
          <SuccessTitle>
            Your request has been received, one of the team will be in touch to confirm the changes
          </SuccessTitle>
          <SuccessButtonGroup>
            <UndoButton onClick={handleUndo}>
              Undo
            </UndoButton>
            <OkButton onClick={handleClose}>
              OK
            </OkButton>
          </SuccessButtonGroup>
        </SuccessDialog>
      ) : (
        <Dialog
          isOpen={true}
          onClose={onClose}
          title={`Edit ${titles[field]}`}
          footer={
            <>
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <SaveButton onClick={handleSave}>Save Changes</SaveButton>
            </>
          }
        >
          {options[field].map((option) => (
            <PaymentOptionButton
              key={option}
              selected={selectedOption === option}
              onClick={() => setSelectedOption(option)}
            >
              {option}
            </PaymentOptionButton>
          ))}
        </Dialog>
      )}
    </Overlay>
  );
};

const PolicyFinancials = ({ policyData, onSave }) => {
  const [editDialog, setEditDialog] = useState({ isOpen: false, field: null, currentValue: null });

  const handleEdit = (field) => {
    setEditDialog({
      isOpen: true,
      field,
      currentValue: policyData.payment[field]
    });
  };

  const handleSave = (field, value) => {
    onSave(field, value);
  };

  const handleClose = () => {
    setEditDialog({ isOpen: false, field: null, currentValue: null });
  };

  return (
    <ContentSection>
      <SectionTitle>Financial Details</SectionTitle>
      <Grid>
        <InfoCard>
          <InfoCardHeader>
            <InfoLabel>Premium Amount</InfoLabel>
          </InfoCardHeader>
          <InfoValue>{policyData.premium.amount}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoCardHeader>
            <InfoLabel>Payment Schedule</InfoLabel>
            <EditButton onClick={() => handleEdit('schedule')}>✎</EditButton>
          </InfoCardHeader>
          <InfoValue>{policyData.premium.schedule}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoCardHeader>
            <InfoLabel>Payment Method</InfoLabel>
            <EditButton onClick={() => handleEdit('method')}>✎</EditButton>
          </InfoCardHeader>
          <InfoValue>{policyData.payment.method}</InfoValue>
        </InfoCard>
      </Grid>

      <PaymentDialog
        isOpen={editDialog.isOpen}
        onClose={handleClose}
        onSave={handleSave}
        field={editDialog.field}
        currentValue={editDialog.currentValue}
      />
    </ContentSection>
  );
};

export default PolicyFinancials; 